import React from 'react'
import { IntlFormatters, MessageDescriptor } from 'react-intl'

import {
  CostMessagingModalContent,
  CoverageType,
  CustomEligibilityMetadata,
  EligibilityType,
  PATHWAYS_TYPE,
  SessionsCoverageText,
} from '../../features/common/constants/constants'
import { CustomerName, CustomerRedirectionProps } from '../../features/common/constants/customer'
import { TREATMENT_OPTIONS_TYPE } from '../../features/common/constants/treatmentOptions'
import { TREATMENT_RECOMMENDATION_TIER } from '../../features/common/constants/treatmentRecommendationsConstants'
import { LocationMatcherDisplayFormats } from '../searchForCare/SearchForCare'
import { SessionCountByProgram, SessionLimitByProgram } from '../session/SessionUsage'
import { EligiblePartnerTreatmentDisplayRules } from '../treatments/PartnerTreatments'
import { CustomerPropertiesMap, ProgramCoverageBreakdown } from '../user/User'
export interface CustomerInfo {
  label: string
  value: string
  internal_customer: boolean
  lyra_code_enabled: boolean | string
  lyra_code_required: boolean | string
  session_count_starts_on: string
  blended_care_meds_enabled: string
  hide_dependent_field: boolean | string
  check_adult_dependent_eligibility_using_dependent_info: boolean | string
  check_child_dependent_eligibility_using_dependent_info: boolean | string
  customer_aliases?: string[]
  display_visits_per_issue_per_year?: number
  display_visits_per_issue_per_year_by_program?: SessionCountByProgram
  eap_eligibility_check_enabled?: string
  lyra_nicotine_quitline_enabled?: boolean | string
  sessions_limit_by_program?: SessionLimitByProgram
  program_coverage_breakdown?: ProgramCoverageBreakdown
  is_one_microsite_member_subpopulation_enabled?: string
  is_subpopulation?: boolean // True for subpopulation (non-primary) customer which has enabled isOneMicrositeMemberSubpopulationEnabled customer property
  gen_ai_opt_out?: string
  library_disabled?: boolean
}

export interface SessionType {
  value: string
  label: string
}

export enum CUSTOMER_CONFIGURATION {
  EAP_ONLY = 'EAP only',
  EAP_PLUS_HPI = 'EAP plus HPI',
  HPI_ONLY = 'HPI only',
}

// export type CustomerTreatmentsOptionLocation = typeof TREATMENT_RECOMMENDATION_TIER & typeof SeeMoreLocation
enum SeeMoreLocation {
  SEE_MORE = 'seeMore',
}
// export const CustomerTreatmentsOptionLocation = { ...TREATMENT_RECOMMENDATION_TIER, ...SeeMoreLocation }
export type CustomerTreatmentsOptionLocation = TREATMENT_RECOMMENDATION_TIER | SeeMoreLocation
export interface CustomerTreatmentOption {
  option: TREATMENT_OPTIONS_TYPE
  config: {
    customCopy?: {
      title: string | JSX.Element
      description: string | JSX.Element
      buttonText: string | JSX.Element
      url: string
    }
    locationsDropdown?: {
      options: { label: string; value: string }[]
      label: MessageDescriptor
      placeholder: MessageDescriptor
      ctaClickHandler: (dropdownValue: string) => void
    }
    footnote?: string | JSX.Element
    key?: string // name of treatment option, e.g. "CROSSOVER_HEALTH", used for filtering/excluding treatment options
    minAge?: number
    iconName?: string // icon name for custom illustration, e.g. "therapy", "coaching", "meds"
  }
  displayRules?: EligiblePartnerTreatmentDisplayRules
  /** Location -> Position where the treatment option should appear relative to the options in `treatmentOptions.tsx` */
  optionRanking: {
    [key in CustomerTreatmentsOptionLocation]?: number
  }
}

// number: the required count for type of character
export interface PasswordPolicy {
  minUpperCase: number
  minLowerCase: number
  minNumber: number
  minLength: number
}

// boolean: if the requirement is met
export interface PasswordPolicyMet {
  minUpperCase: boolean
  minLowerCase: boolean
  minNumber: boolean
  minLength: boolean
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minUpperCase: 1,
  minLowerCase: 1,
  minNumber: 1,
  minLength: 8,
}

interface WorkLifeIdentifiers {
  id: string
  dataTestId: string
}

export interface WorkLifeServicesShape {
  identifiers: WorkLifeIdentifiers
  title: string
  illustrationType?: string
  paragraph?:
    | string
    | (({
        customerName,
        workLifeServiceDependentCareCode,
      }: {
        customerName: string
        workLifeServiceDependentCareCode?: string
      }) => string)
    | React.ReactNode
  bullets: Array<string | React.ReactNode>
  CTA?: string
  PATH?: string
  iconSvg?: React.ReactNode
}

export interface CustomerWithoutMeds {
  legalNotice: React.ReactNode
  servicesNotCovered: string
}

interface FAQQuestion {
  question: string
  answer: React.ReactNode
}

export interface CustomerSubpopulationOption {
  label: string | MessageDescriptor | React.ReactNode
  description: string | MessageDescriptor | React.ReactNode
  customerName?: string // need to be configured for isOneMicrositeMemberSubpopulationEnabled customers, which do not use customerInstanceMap
}

export interface CustomerSubpopulations {
  header: string | MessageDescriptor | React.ReactNode
  subHeader?: string | MessageDescriptor | React.ReactNode
  options: Array<CustomerSubpopulationOption>
}

export interface CustomerRedirection {
  header: MessageDescriptor
  checkboxLabel: MessageDescriptor
  redirectionText: MessageDescriptor
  redirectionLinkPress: () => void
  shouldShowBanner: boolean
}

export interface SMSConsentText {
  primaryConsentText: React.ReactNode
}

export interface GetCostMessagingModalTextParams {
  isDependentDisabled?: boolean
  isCustomerEAPMeds?: boolean
  isCustomerHPIMeds?: boolean
  isCustomerProgramEAPCoaching?: boolean
  isCustomerProgramHPITherapy?: boolean
  isCustomerProgramEAPTherapy?: boolean
  customerName?: string
}

// These properties have been copied over from the default customer copy in the exact order it appears in LW
export interface CustomerCopy {
  /* Flag to indicate whether or not we want to display the session limit to users as part of a treatment option.
   * True means the user will see the string "Your sessions are covered, up to x sessions" where x is the value of
   * the customer property `numberOfSessions` for that given customer.
   * False means the user will be the string "Your session costs are covered".
   *
   * In most cases, this will be true, but for our older "default" customers, this should start as false since they
   * effectively have "unlimited" sessions.
   */
  displaySessionLimit?: boolean
  /* The "default" list of FAQ a customer will have.
   * Each item in this list should represent an FAQ item and can either be a string or an object.
   * Specifying a string means the question and answer pair corresponds to one that can be found
   * in the QuestionGenerator class from FaqView.js.
   * Specifying an object means that the question differs in some fashion - i.e. question text or answer text.
   * Object attributes:
   ** question: The text that should be displayed for the question in the FAQ. Required.
   ** answer: The text that should be displayed for the answer in the FAQ. Required if `key` is not present.
   ** key: A string representing the question from QuestionGenerator that this item should correspond to. Specifying
   *       this attribute means that the answer should be pulled from QuestionGenerator, but the `answer` attribute,
   *       if given, will override this. The key is useful to identify questions that are contextually the same
   *       across customers even when the question and answers differ. Optional.
   *
   * In reality, every customer has little idiosyncrasies about their FAQ, so the following list will rarely be the
   * displayed FAQ. Please look at other customer copy files for more examples of FAQ lists.
   */
  faqQuestions?: (string | FAQQuestion)[]
  showEligibilityQuestionForInternationalRegistration?: boolean
  generateEligibilityQuestionTitle?: () => MessageDescriptor | string
  generateEligibilityDependentTitle?: () => MessageDescriptor | string
  generateRegistrationFormSubHeaderText?: () => MessageDescriptor | string
  generateConfirmEligibilityFormSubHeaderText?: () => MessageDescriptor | string
  generateConfirmEligibilityFormDefaultError?: (
    isHealthPlanDirect?: boolean,
    hasBlockingEligibilityCheck?: boolean,
  ) => React.ReactNode
  eligibilityQuestionSubtitle?: MessageDescriptor | null
  accountLockedText?: MessageDescriptor
  dependentButtonText?: MessageDescriptor
  dependentIneligibleText?: React.ReactNode
  legalNotice?: React.ReactNode
  // TODO: In LW this is called locationMatcher - Replace when all customers have been moved over
  locationFinder?: {
    onsiteFormat: LocationMatcherDisplayFormats
    onsiteDescription: React.ReactNode
  }
  modalityQuestionnaireText?: {
    modalityLabelAddendums: {
      video: MessageDescriptor | string
    }
  }
  passwordResetFailedText?: MessageDescriptor
  servicesNotCovered?: string | React.ReactNode
  servicesCovered?: string
  shouldDisplayCareTeamContactInfo?: boolean
  shouldDisplayBCTProviderBenefits?: boolean
  smsConsent?: SMSConsentText
  eligibilityMetadata?: CustomEligibilityMetadata
  /* Worklife services that a customer can access, assuming full eligibility.
   * Not all customers have access to all services, but this is determined on component render, so we provide all
   * possible service copies to the customer without taking actual offered services into account.
   *
   * Customers can customize their service copy by passing in an array of different services (which will be displayed
   * in the order of items in the array). They can also generate entirely new copy for a given service so long as the
   * ID of the service object maps to a valid worklife service identifier used in the worklife customer property.
   *
   * e.g. This would constitute a valid custom service:
   * { identifiers: WORKLIFE_SERVICE_IDS.IDENTITY_THEFT, title: 'Legal Services (for U.S. employees only)', ... }
   * This would not constitute a valid custom service because 'legalServices' is not a valid worklife service ID:
   * { identifiers: { id: 'legalServices' }, title: 'Legal Services (for U.S. employees only)', ... }
   */
  workLifeServices?: Array<WorkLifeServicesShape>
  workLifeServicesCustomDescription?: string
  workLifeServicesFeaturedDescription?: MessageDescriptor
  hideWorkLifeServicesForInternationalUser?: boolean // do not show WLS for international users
  countriesToHideWorkLifeServicesFor?: string[] // do not show WLS for users in these countries (e.g., 'United Kingdom', 'Ireland')
  treatmentOptions?: Array<CustomerTreatmentOption>
  redirectionCheckbox?: React.ReactNode
  subpopulations?: CustomerSubpopulations
  subpopulations_lctTeens?: CustomerSubpopulations
  customerRedirection?: (customerRedirectionProps: CustomerRedirectionProps) => CustomerRedirection
  generateEmployeeEligibilityError?: (isConfirmEligibility?: boolean, isUnexpectedError?: boolean) => React.ReactNode
  generateEligibilityCheckSubmissionErrors?: (
    eligibilityType?: EligibilityType,
    checkAdultDependentEligibilityUsingDependentInfo?: boolean,
  ) => {
    [fieldName: string]: MessageDescriptor
  }
  customerName?: string
  shouldUseCustomChildCare?: (childDOB: string) => boolean
  highAlertEmergencyInfo?: React.ReactNode
  costMessagingModalText?: {
    [key in CoverageType]: (params: GetCostMessagingModalTextParams) => CostMessagingModalContent[]
  }
  sessionsCoverageText?: SessionsCoverageText
  coverageModalTitleText?: React.ReactNode
  sessionHistoryDescriptionText?: (params: {
    goToCareNavigatorPage: () => void
    benefitEndDate: string
  }) => React.ReactNode
  noCostCareOptionsTitle?: React.ReactNode
  eligibilitySubtitleText?: (benefitEndDate: string) => React.ReactNode
  sessionInventoryText?: (params: {
    displayVisitsPerIssuePerYear?: number
    formatMessage: IntlFormatters['formatMessage']
    isMinor: boolean
    remainingSessionCount: number
    remainingSessionCountByProgram?: SessionCountByProgram
  }) => string | null
  customerDisplayName?: string
  eapEligibilityBannerExpiration?: number
  scheduleResourceCoordinatorCallLink?: string
  userMinAgeRequirement?: number
  customUnderageErrorWidget?: string
  disableSubmitOnDependentSelection?: boolean
  showCountrySelector?: boolean
}

export interface CustomerCopyParams extends CustomerPropertiesMap {
  customerName?: string
  eligibilityType?: string
  customerDisplayName?: string
  isCustomerDynamicSessionLimitEnabled?: boolean
  isLyraForLyrians2026Enabled?: boolean
}

export type GetCustomerCopyFunc = (params: CustomerCopyParams) => CustomerCopy

export type CustomerCopyShape = {
  copy: GetCustomerCopyFunc
  template?: CustomerWithoutMeds | GetCustomerCopyFunc
}

export type CustomerConstantsMap = Record<string, CustomerCopyShape>

export enum MicrositeConfigSectionNames {
  HERO = 'hero',
  MOBILE_APP_PROMO_BANNER = 'mobileAppPromoBanner',
  OFFERINGS_CAROUSEL = 'offeringsCarousel',
  MOBILE_APP_PROMO = 'mobileAppPromo',
  SELF_CARE = 'selfCare',
  TESTIMONIALS = 'testimonials',
  LYRA_MEMBERSHIP = 'lyraMembership',
  CARE_NAVIGATOR = 'careNavigator',
  FAQ = 'faq',
  CUSTOMER_BRANDED_BANNER = 'customerBrandedBanner',
  LYRA_SELECT_HERO = 'lyraSelectHero',
}

export const CUSTOMERS_WITH_BRANDED_BANNERS: string[] = [CustomerName.INSIGHT_GLOBAL]

export interface MicrositeConfig {
  [MicrositeConfigSectionNames.HERO]: {
    pathwaysType: PATHWAYS_TYPE | undefined
  }
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO_BANNER]: {
    hideSection: boolean
  }
  [MicrositeConfigSectionNames.CUSTOMER_BRANDED_BANNER]: {
    hideSection: boolean
  }
  [MicrositeConfigSectionNames.OFFERINGS_CAROUSEL]: {
    hideSection: boolean
    hideMeetWithExpert: boolean
    hideWellnessResources: boolean
    hideAllOfferings: boolean
  }
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO]: {
    hideSection: boolean
  }
  [MicrositeConfigSectionNames.SELF_CARE]: {
    hideSection: boolean
  }
  [MicrositeConfigSectionNames.TESTIMONIALS]: {
    hideSection: boolean
  }
  [MicrositeConfigSectionNames.LYRA_MEMBERSHIP]: { hideSection: boolean; hideNumberOfSessions: boolean }
  [MicrositeConfigSectionNames.CARE_NAVIGATOR]: {
    hideSection: boolean
  }
  [MicrositeConfigSectionNames.FAQ]: {
    hideSection: boolean
  }
}

export const DEFAULT_MICROSITE_CONFIG: MicrositeConfig = {
  [MicrositeConfigSectionNames.HERO]: {
    pathwaysType: undefined,
  },
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO_BANNER]: {
    hideSection: false,
  },
  [MicrositeConfigSectionNames.CUSTOMER_BRANDED_BANNER]: {
    hideSection: true,
  },
  [MicrositeConfigSectionNames.OFFERINGS_CAROUSEL]: {
    hideSection: false,
    hideMeetWithExpert: false,
    hideWellnessResources: false,
    hideAllOfferings: false,
  },
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO]: {
    hideSection: false,
  },
  [MicrositeConfigSectionNames.LYRA_MEMBERSHIP]: { hideSection: false, hideNumberOfSessions: false },
  [MicrositeConfigSectionNames.SELF_CARE]: {
    hideSection: false,
  },
  [MicrositeConfigSectionNames.TESTIMONIALS]: {
    hideSection: false,
  },
  [MicrositeConfigSectionNames.CARE_NAVIGATOR]: {
    hideSection: false,
  },
  [MicrositeConfigSectionNames.FAQ]: {
    hideSection: false,
  },
}

type LyraSelectMicrositeProperties = {
  hideSection: boolean
  backgroundColor: string | undefined
}

const DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES = {
  hideSection: false,
  backgroundColor: undefined,
}

export interface LyraSelectMicrositeConfig {
  [MicrositeConfigSectionNames.LYRA_SELECT_HERO]: LyraSelectMicrositeProperties
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO_BANNER]: LyraSelectMicrositeProperties
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO]: LyraSelectMicrositeProperties
  [MicrositeConfigSectionNames.SELF_CARE]: LyraSelectMicrositeProperties
  [MicrositeConfigSectionNames.LYRA_MEMBERSHIP]: LyraSelectMicrositeProperties
  [MicrositeConfigSectionNames.CARE_NAVIGATOR]: LyraSelectMicrositeProperties
  [MicrositeConfigSectionNames.FAQ]: LyraSelectMicrositeProperties
}

export const DEFAULT_LYRA_SELECT_MICROSITE_CONFIG: LyraSelectMicrositeConfig = {
  [MicrositeConfigSectionNames.LYRA_SELECT_HERO]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO_BANNER]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
  [MicrositeConfigSectionNames.SELF_CARE]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
  [MicrositeConfigSectionNames.MOBILE_APP_PROMO]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
  [MicrositeConfigSectionNames.LYRA_MEMBERSHIP]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
  [MicrositeConfigSectionNames.CARE_NAVIGATOR]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
  [MicrositeConfigSectionNames.FAQ]: DEFAULT_LYRA_SELECT_MICROSITE_PROPERTIES,
}
